import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MessageSquare, Layers, BookOpen, Factory, TrendingUp, ArrowUpRight } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState({ services: 0, blogs: 0, industries: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/contacts/stats`, { headers: authHeader() }),
      axios.get(`${API}/services?admin=true`),
      axios.get(`${API}/blogs`),
      axios.get(`${API}/industries`),
    ])
      .then(([statsRes, servicesRes, blogsRes, industriesRes]) => {
        setStats(statsRes.data);
        setCounts({
          services: Array.isArray(servicesRes.data) ? servicesRes.data.length : 0,
          blogs: Array.isArray(blogsRes.data) ? blogsRes.data.length : 0,
          industries: Array.isArray(industriesRes.data) ? industriesRes.data.length : 0,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    {
      label: "Total Inquiries",
      value: stats?.total ?? "—",
      sub: `${stats?.new ?? 0} new`,
      icon: MessageSquare,
      color: "from-[#7B2CBF] to-[#FF007F]",
      to: "/ngxp-admin/contacts",
    },
    { label: "Services", value: counts.services, icon: Layers, color: "from-[#00A8E8] to-[#7B2CBF]", to: "/ngxp-admin/services" },
    { label: "Blog Posts", value: counts.blogs, icon: BookOpen, color: "from-[#FF007F] to-[#7B2CBF]", to: "/ngxp-admin/blogs" },
    { label: "Industries", value: counts.industries, icon: Factory, color: "from-[#7B2CBF] to-[#00A8E8]", to: "/ngxp-admin/industries" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white font-display">Dashboard</h1>
        <p className="text-gray-500 mt-1 text-sm">Overview of NGXP content and inquiries</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(({ label, value, sub, icon: Icon, color, to }) => (
          <Link
            key={label}
            to={to}
            className="group bg-[#0D0E13] border border-white/5 rounded-2xl p-6 relative transition-all hover:border-[#7B2CBF]/40 hover:bg-[#10121A] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(123,44,191,0.5)]"
          >
            <div className="absolute top-4 right-4 text-gray-600 group-hover:text-[#7B2CBF] transition-colors">
              <ArrowUpRight className="w-4 h-4" />
            </div>
            <div
              className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="text-3xl font-bold text-white font-display">{value}</div>
            <div className="text-sm text-gray-400 mt-1">{label}</div>
            {sub && <div className="text-xs text-[#00A8E8] mt-1">{sub}</div>}
          </Link>
        ))}
      </div>

      {/* Contact status breakdown */}
      {stats && (
        <div className="bg-[#0D0E13] border border-white/5 rounded-2xl p-6">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#7B2CBF]" />
            Contact Inquiry Status
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "New", value: stats.new, color: "text-[#00A8E8]", bg: "bg-[#00A8E8]/10 border border-[#00A8E8]/20" },
              {
                label: "Reviewed",
                value: stats.reviewed,
                color: "text-yellow-400",
                bg: "bg-yellow-400/10 border border-yellow-400/20",
              },
              {
                label: "Replied",
                value: stats.replied,
                color: "text-green-400",
                bg: "bg-green-400/10 border border-green-400/20",
              },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`rounded-xl p-4 ${bg} text-center`}>
                <div className={`text-2xl font-bold font-display ${color}`}>{value}</div>
                <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
